import React from "react";
import { IonAlert } from "@ionic/react";

interface AcaSettingsCreateAlertFlowProps {
    isPromptOpen: boolean;
    setPromptOpen: (value: boolean) => void;
    isConfirmOpen: boolean;
    setConfirmOpen: (value: boolean) => void;
    confirmQueued: boolean;
    setConfirmQueued: (value: boolean) => void;
    pendingValue: string;
    setPendingValue: (value: string) => void;
    initialValue: string;
    existingValues: string[];
    promptHeader: string;
    inputName: string;
    inputPlaceholder: string;
    emptyMessage: string;
    duplicateMessage: (value: string) => string;
    confirmHeader: string;
    confirmMessage: (value: string) => string;
    confirmButtonText: string;
    presentAlert: (options: any) => void;
    onConfirm: (value: string) => boolean | Promise<boolean>;
}

const normalizeDupKey = (value: any): string => {
    return (value ?? "").toString().trim().toLowerCase();
};

const AcaSettingsCreateAlertFlow: React.FC<AcaSettingsCreateAlertFlowProps> = ({isPromptOpen, setPromptOpen, isConfirmOpen, setConfirmOpen, confirmQueued, setConfirmQueued, pendingValue, setPendingValue, initialValue, existingValues, promptHeader, inputName, inputPlaceholder, emptyMessage, duplicateMessage, confirmHeader, confirmMessage, confirmButtonText, presentAlert, onConfirm,}) => {
    return (
        <>
            <IonAlert
                isOpen={isPromptOpen}
                header={promptHeader}
                inputs={[
                    {
                        name: inputName,
                        type: "text",
                        placeholder: inputPlaceholder,
                        value: pendingValue || initialValue,
                    },
                ]}
                buttons={[
                    { text: "Cancel", role: "cancel" },
                    {
                        text: "Next",
                        role: "confirm",
                        handler: (values) => {
                            const value = (values?.[inputName] || "").trim();
                            if (!value) {
                                presentAlert(emptyMessage);
                                return false;
                            }

                            const valueKey = normalizeDupKey(value);
                            const isDup = existingValues.some(
                                (existingValue) => normalizeDupKey(existingValue) === valueKey
                            );
                            if (isDup) {
                                presentAlert(duplicateMessage(value));
                                return false;
                            }

                            setPendingValue(value);
                            setConfirmQueued(true);
                            return true;
                        },
                    },
                ]}
                onDidDismiss={(ev) => {
                    setPromptOpen(false);

                    if (ev.detail.role === "confirm" && confirmQueued) {
                        setConfirmQueued(false);
                        setConfirmOpen(true);
                        return;
                    }
                    setConfirmQueued(false);
                }}
            />

            <IonAlert
                isOpen={isConfirmOpen}
                header={confirmHeader}
                message={confirmMessage(pendingValue)}
                onDidDismiss={() => {
                    setConfirmOpen(false);
                }}
                buttons={[
                    {
                        text: "Back",
                        role: "cancel",
                        handler: () => {
                            setConfirmOpen(false);
                            setPromptOpen(true);
                        },
                    },
                    {
                        text: confirmButtonText,
                        role: "confirm",
                        handler: () => {
                            void (async () => {
                                const success = await onConfirm(pendingValue);
                                if (success) {
                                    setPendingValue("");
                                    setConfirmOpen(false);
                                }
                            })();
                            return false;
                        },
                    },
                ]}
            />
        </>
    );
};

export default AcaSettingsCreateAlertFlow;